import {getGeometry, diameter, centroid} from "effects/midi/mappable/lang/geometry.js";
import {makeHull} from './convexHull.js';
import ParameterContext from "effects/ParameterContext.js";
import SubMacro from 'effects/SubMacro.js';
import {AbstractAudioParamParameter} from 'parameters/AbstractAudioParamParameter.js';
import Tone from 'tone';
import {getCoordinates} from './getCoordinates.js';
import {WIDTH, HEIGHT, PADDING} from 'components/effects/plane/constants.js';
import {uuid} from 'uuidv4';
import {setPlaneMacros} from 'actions/index.js';

export default superclass => class extends superclass  {
    macros = [];

    deleteMacro(macro) {
        this.macros =  this.macros.filter(x => x !== macro);
        this.updateMacroRedux();
        macro.dispose();
    }

    createMacro(parameters) {
        let name = "Macro " + (this.macros.length + 1);
        let macro = new Macro(name, parameters, this.macros.length + 1, this);
        parameters.forEach(
            parameter => parameter.planeMacro = macro);
        this.addMacro(macro);
   }

    freeMacro(macro) {
        macro.free();
        this.deleteMacro(macro);
    }

    updateMacroRedux() {
        this.track.sequencerEngine.dispatch(
            setPlaneMacros(
                this.id,
                this.macros));
    }

    addMacro(macro) {
        this.macros = [... this.macros, macro];
        this.updateMacroRedux();
    }

    getMacrosJson() {
        return this.macros.map(x => x.getJson());
    }

    fromMacrosJson(json) {
        if (!json) {
            return;
        }
        for (let m of json) {
            let macro = new Macro("macro", [], 0, this);
            macro.fromJson(m);
            this.addMacro(macro);
        }
    }
}

/**
 * A macro in a Plane Effect, is simply a way of reducing
 * many parameters (on the 2D plane) into 1 point.
 * Geometrically, it is the centroid of all the points
 * Thus when the (plane group) algorithm alters the underlying parameters
 * the centroid might move
 */
class Macro {
    constructor(name, parameters, number, effect) {
        this.id = uuid();
        this.name = name;
        this.number = number;
        this.parameters = parameters;
        this.effect = effect;
        this.signal = new Tone.Signal(0);
        this.signalParameter = new AbstractAudioParamParameter(
            'macro',
            this.signal, () => {},
            {
                min: 0,
                defaultValue: 0,
                max: 1,
            });
        this.signalParameter.setContext(
            new ParameterContext().forTrack(this.effect.track));
        this.subMacros = {};
    }

    dispose() {
        let lfos = this.effect.track.sequencerEngine.lfos.map(x => x.lfo);
        for (let lfoNumber in this.subMacros) {
            let lfo = lfos.find(l => l.number === parseInt(lfoNumber));
            this.disconnectFromLFO(lfo);
        }
        this.parameters.forEach(
            p => p.planeMacro = undefined);
        this.signalParameter.dispose();
        this.signal.dispose();
        this.signalParameter = undefined;
        this.subMacros = undefined;
        this._disposed = true;
    }

    disposeSubMacro(m) {
        let parameter = m.parameter;
        this.signal.disconnect(m.scaler);
        m.dispose();
        parameter.set(parameter.get(true), true, true);
    }

    disconnectFromLFO(lfo) {
        let subMacros = this.subMacros[lfo.number];
        subMacros.forEach(m => {
            this.disposeSubMacro(m);
        });
        delete subMacros[lfo.number];
        this.signalParameter.disconnectFromLFO(lfo);
    }

    connectToLFO(lfo, connectLFO=true) {
        // first connect signal to each parameter
        let parameters = this.parameters
            .filter(
                // ensure its not already connected
                p => !this.subMacros[lfo.number] ||
                    !this.subMacros[lfo.number].some(m => m.parameter === p))
            .filter(
                // we dont want to connect an lfo to itself
                // as it will cause feedback problems and 'essplode
                p => p.context &&
                    (!p.context.lfoTrack ||
                     p.context.lfoTrack.lfo !== lfo))
            .filter(
                // this tends to be a problem
                p => p.name !== "LFO Max" &&
                    p.name !== "LFO Phase")
            .filter(
                p => p.audioParam || p.audioParams);
        let alreadyConnected = this.subMacros[lfo.number] !== undefined;
        let subMacros = this.subMacros[lfo.number] || [];
        this.subMacros[lfo.number] = [
            ... subMacros,
            ...parameters.map(
                p => {
                    let subMacro = new SubMacro(
                        p, p.min, p.max);
                    subMacro.withSignal(this.signal);
                    
                    p.set(p.get(true), true, true);
                    return subMacro;
                })
        ];

        // need to make sure we arent already connected
        if (connectLFO && !this.lfos.includes(lfo)) {
            this.signalParameter.connectToLFO(lfo);
        }
    }

    fromJson(json, isCopy) {
        this.name = json.name;
        this.id = json.id;
        this.number = json.number;
        this.parameterIds = json.parameters;
        this.signalParameterJson = json.signalParameter;
        this.isCopy = isCopy;
   }

    loadParameters(parameters, newMappings) {
        let parameterIds = this.parameterIds.map(
            id => newMappings[id] ? newMappings[id].id : id);
        for (let parameter of parameters) {
            if (parameterIds.includes(parameter.id) &&
                !this.parameters.includes(parameter)) {
                this.parameters.push(parameter);
                parameter.planeMacro = this;
            }
        }

        if (this.signalParameterJson) {
            // this essentially loads the lfos/modulation depths
            // for the signalParameter
            if (!this._loadedSignalParameter) {
                this.signalParameter.fromJson(
                    this.signalParameterJson,
                    this.effect.track.sequencerEngine,
                    this.isCopy);
                this._loadedSignalParameter = true;
            }

            for (let lfo of this.signalParameter.lfos) {
                // since we already loaded the lfos we just
                // need to set up the sub macros wrt parameters
                this.connectToLFO(lfo, false);
            }
        }
    }

    get lfos() {
        return this.signalParameter.lfos;
    }

    free() {
        this.parameters.forEach(
            p => this.removeParameter(p));
    }

    getJson() {
        return {
            name: this.name,
            id: this.id,
            number: this.number,
            parameters: this.parameters.map(x => x.id),
            signalParameter: this.signalParameter.getJson()
        };
    }

    removeParameter(parameter) {
        if (this._disposed) {
            // already disposed
            return;
        }
        this.parameters = this.parameters.filter(
            x => x !== parameter);
        parameter.planeMacro = undefined;

        // find all sub macros we need to disconect
        let subMacrosToDelete = Object.values(this.subMacros).flat().filter(
            m => m.parameter === parameter);

        subMacrosToDelete.forEach(m => this.disposeSubMacro(m));
        
        // remove them from this.subMacros
        for (let lfo in this.subMacros) {
            this.subMacros[lfo] = this.subMacros[lfo].filter(
                m => !subMacrosToDelete.includes(m));
        }
    }

    get isPlaneMacro() {
        return true;
    }

    isEmpty() {
        return this.parameters.length === 0;
    }

    // so that it implements 2DParammable
    getCoordinates() {
        return this.coordinates;
    }

    get coordinates() {
        if (this.parameters.length === 0) {
            return 0;
        }
        let allCoords = this.parameters.map(
            p => p.getCoordinates());
        this._coordinates = centroid(allCoords);
        return this._coordinates;
    }

    get diameter() {
        return diameter(
            this.parameters.map(p => p.getCoordinates()));
    }

    getAllCoordinates() {
        return getCoordinates(this.parameters);
    }

    getGeometry() {
        if (this.parameters.length === 0) {
            return {x:0, y:0, diameter:0};
        }
        let coordinates = this.getAllCoordinates();
        return getGeometry(coordinates);
   }

    setCoordinates(x1,y1) {
        // find the last coordinates
        let {x, y} = this._coordinates;
        let xDiff = x1-x;
        let yDiff = y1-y;
        this.moveCoordinates(xDiff, yDiff);
    }

    moveCoordinates(...args) {
        let results = this.parameters.map(
            x => x.moveCoordinates(... args));
        // need to update redux
        this.effect.setEffectParameterValues(
            results.reduce((a,{id, value}, {}) => {
                a[id] = value;
                return a;
            }));
    }

    getPath(x,y) {
        let coords = [];
        for (let parameter of this.parameters) {
            let c = parameter.getCoordinates();
            coords.push(c);
        }
        let convexHull = makeHull(coords);
        let first = convexHull[0];
        if (!first) {
            return "";
        }
        let d = "M " + first.x + ', ' + first.y + 'C' +
            convexHull.slice(1).map(({x,y}) => x + ',' + y).join(',') + 'Z';
        return d;

    }
}

const dist = (x1,y1,x2,y2) => {
    return Math.sqrt(Math.pow(x1-x2, 2) + Math.pow(y1-y2, 2));
};
